import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { MouseEvent, useEffect, useState } from 'react';
import { TextField, Tooltip } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import FundraiserTitle from 'src/components/UI/FundRaiserTitle';
import Row from 'src/components/UI/Row';
import Scroller from 'src/components/UI/Scroller';
import { makeStyles } from '@material-ui/core/styles';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import Subtitle from 'src/components/UI/Subtitle';
import ContactSelector from 'src/components/Control/ContactSelector';
import { AUTOCOMPLETE_ID } from 'src/constants/Autocomplete';
import { openModal } from 'src/redux/actions/modalActions';
import { MODALS } from 'src/models/ModalModel';
import EditButton from 'src/components/UI/EditButton';
import DeleteButton from 'src/components/Elements/DeleteButton';
import SubmenuLink from 'src/components/UI/SubmenuLink';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import { FORM } from 'src/constants/Form';
import FormRichTextEditor from 'src/components/Control/FormControls/FormRichTextEditor';
import DownloadAmbassadorButton from 'src/components/Control/Buttons/DownloadAmbassadorButton';
import { getAmbassadorListAction } from 'src/redux/actions/ambassadorActions';
import { openDialog } from 'src/redux/actions/dialogActions';
import { DIALOGS } from 'src/models/DialogModel';
import { setError } from 'src/redux/actions/errorsActions';
import { FundraisingProps, renderHasChanged } from 'src/components/Pages/AddFundraisingPage';
// import FundraisingInfoValidator from 'src/validations/FundraisingInformationValidator';
// import { ErrorBag } from 'src/models/ErrorModel';
import { denormalizeFundraisingSection } from 'src/lib/FundraisingHelper';
import { stripAccents } from 'src/lib/StringHelper';
import { FundraisingSections } from 'src/constants/FundraisingTypes';
import {
  Ambassador,
  FundraisingAmbassadors,
  FundraisingInfo,
  FundraisingModel,
  Step,
  TABS,
} from 'src/models/FundraisingModel';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import BottomRow from 'src/components/UI/BottomRow';
import Line from 'src/components/UI/Line';
import InlineHelp from 'src/components/UI/InlineHelp';
import SendAmbassadorInviteButton from 'src/components/Control/Buttons/SendAmbassadorInviteButton';
import EditAmbassadorMessageButton from 'src/components/Control/Buttons/EditAmbassadorMessageButton';
import palette from 'src/styles/palette';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import EmailIcon from '@material-ui/icons/Email';
import SmsIcon from '@material-ui/icons/Sms';

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    alignItems: 'start',
    gap: '8px',
    width: '100%',
  },
  columns: {
    display: 'flex',
    flexDirection: 'column',
  },
  textField: {
    width: '148px',
  },
  large: {
    width: '573px',
  },
  col3: {
    display: 'flex',
    width: '32.5%',
  },
  url: {
    fontSize: '12px',
    marginTop: '-4px',
    color: '#333',
    marginLeft: '2px',
  },
}));

const FundraisingAmbassadorsForm = (props: FundraisingProps) => {
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const {
    onChange,
    setHasChanged,
    setErrors,
    fundraisingState,
    hasChanged,
  } = props;

  useEffect(() => {
    if (!fundraisingState.fundraisingInfo?.id) return;
    dispatch(getAmbassadorListAction(fundraisingState.fundraisingInfo.id));
  }, []);

  enum TAB {
    setting = 'setting',
    ambassador = 'ambassador',
  }

  const [currentFilter, setCurrentFilter] = useState('');
  const [ambassadorState, setAmbassadorState] = useState(
    fundraisingState.ambassadors || {} as FundraisingAmbassadors,
  );
  const [contactId, setContactId] = useState(0);
  const [filterEmail, setFilterEmail] = useState(false);
  const [filterSMS, setFilterSMS] = useState(false);
  const [tab, setTab] = useState(TAB.setting);

  const [selections, setSelections] = useState<string[]>([]);

  const ambassadors = useSelector(
    (state: Store) => state.ambassadors,
  );

  const [wordCount, setWordCount] = useState(0);

  useEffect(() => {
    if (!fundraisingState.ambassadors) return;
    setAmbassadorState(fundraisingState.ambassadors);
  }, [fundraisingState]);

  const onAmbassadorsChange = (value: any, field: string) => {
    setHasChanged(true);
    if (field === 'message') {
      if (value.length > 1200) {
        return;
      }
      setWordCount(value.length);
    }
    setAmbassadorState({
      ...ambassadorState,
      [field]: value,
    });
  };

  const onAddAmbassador = () => {
    const { slug, id } = fundraisingState.fundraisingInfo ?? {} as FundraisingInfo;
    const exists = ambassadors.some(
      (ambassador) => ambassador.contact_id === contactId,
    );
    if (exists) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Ambassador with same contact already exists',
      }));
      return;
    }
    dispatch(
      openModal(
        {
          modal: MODALS.addAmbassador,
          payload: [
            {} as Ambassador, slug, contactId, id],
        },
      ),
    );
  };

  const handleOnRowClick = (id: string) => {
    const index = selections.indexOf(id);
    if (index >= 0) {
      selections.splice(index, 1);
    } else {
      selections.push(id);
    }
    setSelections([...selections]);
  };

  const handleEdit = (ambassador: Ambassador) => {
    const { slug, id } = fundraisingState.fundraisingInfo ?? {} as FundraisingInfo;
    dispatch(
      openModal(
        {
          modal: MODALS.editAmbassador,
          payload: [ambassador, slug, id],
        },
      ),
    );
  };

  const handleDelete = (id: string | number) => {
    dispatch(openDialog({ dialog: DIALOGS.deleteAmbassador, payload: id }));
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>, advance: boolean) => {
    event.preventDefault();
    dispatch(setError({ [FORM.fundraising]: {} }));
    setErrors(TABS.ambassador, false);
    setHasChanged(false);
    onChange({
      ...fundraisingState,
      ambassadors: denormalizeFundraisingSection(FundraisingSections.ambassadors, ambassadorState),
    } as FundraisingModel,
    Step.ambassadors,
    advance);
  };

  const renderEmailInvite = (sent: boolean) => (
    <Tooltip
      title={sent ? 'Email invitation sent' : 'Email invitation not sent'}
      placement="top-start">
      <EmailIcon style={{ fontSize: '18px', color: sent ? 'green' : 'red', verticalAlign: 'middle' }} />
    </Tooltip>
  );

  const renderSmsInvite = (sent: boolean) => (
    <Tooltip
      title={sent ? 'SMS invitation sent' : 'SMS invitation not sent'}
      placement="top-start">
      <SmsIcon style={{ fontSize: '18px', color: sent ? 'green' : 'red', verticalAlign: 'middle' }} />
    </Tooltip>
  );

  const getItemStyle = (id?: number | string) => {
    if (!id) return {};
    if (selections.indexOf(id.toString()) < 0) {
      return {
        backgroundColor: '#FFF',
      };
    }
    return {
      border: `1px solid ${palette.primary.main}`,
      backgroundColor: lighten(palette.primary.main, 0.9),
    };
  };

  const renderAmbassador = (ambassador: Ambassador) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: '36px',
        gap: '10px',
        border: '1px solid rgba(0, 0, 0, 0.25)',
        padding: '8px',
        borderRadius: '8px',
        marginBottom: '8px',
        ...getItemStyle(ambassador.id),
        cursor: 'pointer', // Visual feedback everywhere
      }}
      onClick={() => handleOnRowClick(ambassador.id?.toString() ?? '')}
      key={`ambassador-${ambassador.id}`}>
      {/* Main content wrapper fills space */}
      <span style={{
        flex: 1, display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0,
      }}>
        {renderEmailInvite(ambassador.invitation_email_sent)}
        {renderSmsInvite(ambassador.invitation_sms_sent)}
        <span style={{ color: palette.secondary.main }}>{ambassador.name}</span>
        <span style={{ marginLeft: 'auto' }}>Goal: {ambassador.goal}</span>
      </span>
      {/* Buttons right-aligned */}
      <span style={{ display: 'flex', gap: '5px' }}>
        <EditButton
          onClick={() => { handleEdit(ambassador); }}
          title="Edit ambassador" />
        <DeleteButton
          onClick={() => { handleDelete(ambassador.id || 0); }}
          title="Delete ambassador" />
      </span>
    </div>
  );

  const renderAmbassadors = () => {
    const normalizedFilter = stripAccents(currentFilter ?? '').toLowerCase().trim();

    const filtered = ambassadors.filter((ambassador) => {
      const nameMatchesFilter = ambassador.name
        && stripAccents(ambassador.name).toLowerCase().includes(normalizedFilter);
      if (normalizedFilter && !nameMatchesFilter) {
        return false;
      }
      if (filterEmail && ambassador.invitation_email_sent) return false;
      if (filterSMS && ambassador.invitation_sms_sent) return false;
      return true;
    });

    return filtered.map(renderAmbassador);
  };

  const getMaxWord = () => `${wordCount}/1200 characters`;

  const fundraisingId = fundraisingState.fundraisingInfo?.id ?? undefined;

  return (
    <form noValidate>
      <FundraiserTitle>Ambassadors and Donors</FundraiserTitle>
      <Row>
        <SubmenuLink
          color="rgb(165, 23, 96)"
          onClick={() => setTab(TAB.setting)}
          selected={tab === TAB.setting}>
          Settings
        </SubmenuLink>
        <SubmenuLink
          color="rgb(165, 23, 96)"
          onClick={() => setTab(TAB.ambassador)}
          selected={tab === TAB.ambassador}>
          Ambassadors
        </SubmenuLink>
      </Row>
      { tab === TAB.setting && (
      <>
        <Subtitle style={{ marginTop: '0px' }}>Settings</Subtitle>
        <div className={classes.row} style={{ width: '100%' }}>
          <div style={{ width: '33%', marginTop: '8px' }}>
            <FormCheckbox
              label="Show the ambassadors section"
              name="show_ambassador_section"
              onChange={(value: boolean) => { onAmbassadorsChange(value, 'show_ambassador_section'); }}
              checked={!!ambassadorState.show_ambassador_section} />
          </div>
          <div style={{ width: '33%', marginTop: '8px' }}>
            <FormCheckbox
              label="Show the ambassadors' goal"
              name="show_ambassador_goal"
              onChange={(value: boolean) => { onAmbassadorsChange(value, 'show_ambassador_goal'); }}
              checked={!!ambassadorState.show_ambassador_goal} />
          </div>
          <div style={{ width: '33%', marginTop: '8px' }}>
            <FormCheckbox
              label="Show the ambassadors' real amount"
              name="show_ambassador_real_amount"
              onChange={(value: boolean) => { onAmbassadorsChange(value, 'show_ambassador_real_amount'); }}
              checked={!!ambassadorState.show_ambassador_real_amount} />
          </div>
        </div>
        <div className={classes.row} style={{ width: '100%' }}>
          <div style={{ width: '33%' }}>
            <FormCheckbox
              label="Show the donors section"
              name="show_donors_section"
              onChange={(value: boolean) => { onAmbassadorsChange(value, 'show_donors_section'); }}
              checked={!!ambassadorState.show_donors_section} />
          </div>
          <div style={{ width: '33%' }}>
            <FormCheckbox
              label="Show the donors' amount"
              name="show_donor_amount"
              onChange={(value: boolean) => { onAmbassadorsChange(value, 'show_donor_amount'); }}
              checked={!!ambassadorState.show_donor_amount} />
          </div>
          <div style={{ width: '33%' }}>
            <FormCheckbox
              label="Show the donors' real amount"
              name="show_donor_real_amount"
              onChange={(value: boolean) => { onAmbassadorsChange(value, 'show_donor_real_amount'); }}
              checked={!!ambassadorState.show_donor_real_amount} />
          </div>
        </div>
        <div className={classes.row} style={{ width: '100%' }}>
          <div style={{ width: '33%' }}>
            <FormCheckbox
              label="Show comments from the donors"
              name="show_donors_comments"
              onChange={(value: boolean) => { onAmbassadorsChange(value, 'show_donors_comments'); }}
              checked={!!ambassadorState.show_donors_comments} />
          </div>
        </div>
        <div className={classes.row} style={{ width: '100%' }}>
          <div style={{ width: '50%', paddingTop: '8px' }}>
            <Subtitle style={{ marginTop: '8px', marginBottom: '16px' }}>
              Email invitation message
            </Subtitle>
            <FormTextField
              form={FORM.fundraising}
              label="Email title"
              name="message_email_title"
              onChange={onAmbassadorsChange}
              margin="dense"
              value={ambassadorState.message_email_title ?? ''} />
            <FormRichTextEditor
              form={FORM.fundraising}
              toolbar="invitation"
              label="Text for email invitation"
              name="message_email"
              onChange={onAmbassadorsChange}
              height="200px"
              value={ambassadorState.message_email ?? ''} />
            <InlineHelp>
              <Line>Please use the following tags in your email:</Line>
              <Line>
                <i>#first-name</i>,&nbsp;
                <i>#last-name</i>,&nbsp;
                <i>#ambassador-name</i>,&nbsp;
                <i>#ambassador-link</i>
              </Line>
              <Line>The system will automatically replace them.</Line>
              <Line><i>Example:</i></Line>
              <Line>Hello #first-name</Line>
              <Line>&nbsp;</Line>
              <Line>You have been invited to be part of a fundraising!</Line>
              <Line>Your ambassador name is: #ambassador-name</Line>
            </InlineHelp>
          </div>
          <div style={{ width: '50%', paddingTop: '8px' }}>
            <Subtitle style={{ marginTop: '8px', marginBottom: '16px' }}>
              SMS invitation message
            </Subtitle>
            <FormTextField
              form={FORM.fundraising}
              style={{ marginTop: '0px' }}
              label="Text for sms invitation"
              name="message_sms"
              onChange={onAmbassadorsChange}
              multiline
              minRows={5}
              maxRows={5}
              value={ambassadorState.message_sms} />
            <div style={{
              fontSize: '12px',
              marginTop: '-4px',
              color: '#333',
              marginLeft: '2px',
            }}>
              {getMaxWord()}
            </div>
          </div>
        </div>
        <BottomRow>
          <div style={{ width: '50%', paddingTop: '8px' }} />
          <div style={{
            width: '50%', paddingTop: '8px', marginTop: '0px', marginBottom: 'auto',
          }}>
            <span style={{ float: 'right' }}>
              <Button
                style={{ marginRight: '8px' }}
                variant="contained"
                color="primary"
                onClick={(e) => handleSubmit(e, false)}
                key="submit-button">
                Save{renderHasChanged(!hasChanged)}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={(e) => handleSubmit(e, true)}
                key="submit-button-next">
                Save{renderHasChanged(!hasChanged)} and Next Step
              </Button>
            </span>
          </div>
        </BottomRow>
      </>
      )}
      { tab === TAB.ambassador && (
      <>
        <Subtitle style={{ marginTop: '0px' }}>Add an Ambassador</Subtitle>
        <Row height="auto">
          <div style={{
            width: '85%',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '8px',
          }}>
            <ContactSelector
              contactId={contactId}
              autocompleteId={AUTOCOMPLETE_ID.edit_fundraising_team}
              onChange={setContactId}
              margin="dense" />
            <Button
              style={{
                marginBottom: '24px',
                marginTop: '8px',
              }}
              variant="contained"
              color="primary"
              onClick={onAddAmbassador}
              disabled={!contactId}>
              Add
            </Button>
          </div>
        </Row>
        <Subtitle style={{ marginTop: '8px' }}>Ambassadors List</Subtitle>
        <Row height="auto">
          <TextField
            margin="dense"
            variant="outlined"
            label="Search Ambassadors"
            onChange={(e) => setCurrentFilter(e.target.value)} />
          <FormCheckbox
            style={{ marginTop: '0px' }}
            label="Email not sent"
            name="filterEmail"
            onChange={(value: boolean) => {
              setFilterEmail(value);
            }}
            checked={!!filterEmail} />
          <FormCheckbox
            style={{ marginTop: '0px' }}
            label="SMS not sent"
            name="filterSMS"
            onChange={(value: boolean) => {
              setFilterSMS(value);
            }}
            checked={!!filterSMS} />
          <span style={{
            marginLeft: 'auto', marginRight: '8px', display: 'flex', gap: '8px',
          }}>
            <DownloadAmbassadorButton fundraisingId={fundraisingId} />
            <SendAmbassadorInviteButton
              selected={selections}
              fundraisingId={fundraisingId}
              messageEmail={ambassadorState.message_email ?? ''}
              messageSms={ambassadorState.message_sms ?? ''} />
            <EditAmbassadorMessageButton selected={selections} fundraisingId={fundraisingId} />
          </span>
        </Row>
        <Scroller height="54vh" style={{ padding: 0 }}>
          {renderAmbassadors()}
        </Scroller>
      </>
      )}
    </form>
  );
};

export default FundraisingAmbassadorsForm;
//
